import { useState, useMemo, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDataPost, fetchDataGet } from "../../controler/comunicacao";
import LoadingModal from "../../components/LoadingModal/LoadingModal.jsx";
import localidades from "../../components/Filter/cidades.jsx";
import "../Dashboard.css";

const STALE = 1000 * 60 * 60 * 24 * 15;
const DEFAULT_ANOS = ["2024","2023","2022","2021","2020","2019","2018","2017","2016","2015","2014","2013","2012"];
const REGIOES = Object.keys(localidades);

const TODOS_ESTADOS = [...new Set(REGIOES.flatMap((r) => Object.keys(localidades[r])))].sort();

function estadosDaRegiao(regiao) {
  if (!regiao) return TODOS_ESTADOS;
  return Object.keys(localidades[regiao] || {}).sort();
}

function municipiosDoEstado(estado) {
  if (!estado) return [];
  const list = [];
  Object.values(localidades).forEach((reg) => {
    if (reg[estado]) list.push(...reg[estado]);
  });
  return list.sort();
}

// ── Filtros ───────────────────────────────────────────────────────────────────
function RankingFilters({ onGerar }) {
  const { data: anosApi } = useQuery({
    queryKey: ['anos-v2'],
    queryFn: () => fetchDataGet('/api/anos'),
    staleTime: STALE,
    refetchOnWindowFocus: false,
    retry: 0,
  });
  const ANOS_OPCOES = anosApi ?? DEFAULT_ANOS;

  const [desfecho,   setDesfecho]  = useState("TODOS");
  const [metrica,    setMetrica]   = useState("INCIDENCIA");
  const [anos,       setAnos]      = useState(["TODOS"]);
  const [regiao,  setRegiao] = useState("");
  const [estado,  setEstado] = useState("");

  const estados    = estadosDaRegiao(regiao);
  const isAbandono = desfecho === "ABANDONO";

  function toggleAno(ano) {
    if (ano === "TODOS") { setAnos(["TODOS"]); return; }
    setAnos((prev) => {
      const sem = prev.filter((a) => a !== "TODOS");
      if (sem.includes(ano)) {
        const next = sem.filter((a) => a !== ano);
        return next.length === 0 ? ["TODOS"] : next;
      }
      return [...sem, ano];
    });
  }

  function handleDesfecho(v) {
    setDesfecho(v);
    if (v === "ABANDONO") setMetrica("ABANDONO");
    else if (metrica === "ABANDONO") setMetrica("ABSOLUTO");
  }

  function handleRegiao(v) {
    setRegiao(v);
    setEstado("");
  }

  function handleEstado(v) {
    setEstado(v);
  }

  return (
    <div className="container-fluid px-0 pb-3">
      <div className="d-flex overflow-auto gap-3 pb-2 align-items-start">

        {/* Desfecho */}
        <div className="card flex-shrink-0" style={{ minWidth: 200, overflowY: "auto", maxHeight: 280 }}>
          <div className="card-body" style={{ padding: "12px 14px", boxShadow: "none" }}>
            <h6 className="card-title mb-2">Desfecho</h6>
            {[
              { v: "TODOS",    l: "Notificações" },
              { v: "OBITO",    l: "Óbito" },
              { v: "ABANDONO", l: "Interrupção do Tratamento" },
            ].map(({ v, l }) => (
              <div className="form-check mb-1" key={v}>
                <input className="form-check-input" type="radio" name="desfecho"
                  id={`d-${v}`} checked={desfecho === v} onChange={() => handleDesfecho(v)} />
                <label className="form-check-label" htmlFor={`d-${v}`}>{l}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Métrica */}
        <div className="card flex-shrink-0" style={{ minWidth: 220, overflowY: "auto", maxHeight: 280 }}>
          <div className="card-body" style={{ padding: "12px 14px", boxShadow: "none" }}>
            <h6 className="card-title mb-2">Métrica</h6>
            {isAbandono ? (
              <div className="form-check mb-1">
                <input className="form-check-input" type="radio" checked readOnly disabled />
                <label className="form-check-label text-muted">Taxa de Abandono (%)</label>
              </div>
            ) : (
              [
                { v: "ABSOLUTO",   l: "Nº Absoluto de Casos" },
                { v: "INCIDENCIA", l: "Incidência / 100k hab." },
              ].map(({ v, l }) => (
                <div className="form-check mb-1" key={v}>
                  <input className="form-check-input" type="radio" name="metrica"
                    id={`m-${v}`} checked={metrica === v} onChange={() => setMetrica(v)} />
                  <label className="form-check-label" htmlFor={`m-${v}`}>{l}</label>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Anos */}
        <div className="card flex-shrink-0" style={{ minWidth: 110, overflowY: "auto", maxHeight: 280 }}>
          <div className="card-body" style={{ padding: "12px 14px", boxShadow: "none" }}>
            <h6 className="card-title mb-2">Ano</h6>
            <div className="form-check mb-1">
              <input className="form-check-input" type="checkbox" id="a-todos"
                checked={anos[0] === "TODOS"} onChange={() => setAnos(["TODOS"])} />
              <label className="form-check-label" htmlFor="a-todos">TODOS</label>
            </div>
            {ANOS_OPCOES.map((ano) => (
              <div className="form-check mb-1" key={ano}>
                <input className="form-check-input" type="checkbox" id={`a-${ano}`}
                  checked={anos.includes(ano)} onChange={() => toggleAno(ano)} />
                <label className="form-check-label" htmlFor={`a-${ano}`}>{ano}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Região */}
        <div className="card flex-shrink-0" style={{ minWidth: 160, overflowY: "auto", maxHeight: 280 }}>
          <div className="card-body" style={{ padding: "12px 14px", boxShadow: "none" }}>
            <h6 className="card-title mb-2">Região</h6>
            <div className="form-check mb-1">
              <input className="form-check-input" type="radio" name="regiao"
                id="r-todas" checked={!regiao} onChange={() => handleRegiao("")} />
              <label className="form-check-label" htmlFor="r-todas">Todas</label>
            </div>
            {REGIOES.map((r) => (
              <div className="form-check mb-1" key={r}>
                <input className="form-check-input" type="radio" name="regiao"
                  id={`r-${r}`} checked={regiao === r} onChange={() => handleRegiao(r)} />
                <label className="form-check-label" htmlFor={`r-${r}`}>{r}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Estado */}
        <div className="card flex-shrink-0" style={{ minWidth: 120, overflowY: "auto", maxHeight: 280 }}>
          <div className="card-body" style={{ padding: "12px 14px", boxShadow: "none" }}>
            <h6 className="card-title mb-2">Estado</h6>
            <div className="form-check mb-1">
              <input className="form-check-input" type="radio" name="estado"
                id="e-todos" checked={!estado} onChange={() => handleEstado("")} />
              <label className="form-check-label" htmlFor="e-todos">Todos</label>
            </div>
            {estados.map((e) => (
              <div className="form-check mb-1" key={e}>
                <input className="form-check-input" type="radio" name="estado"
                  id={`e-${e}`} checked={estado === e} onChange={() => handleEstado(e)} />
                <label className="form-check-label" htmlFor={`e-${e}`}>{e}</label>
              </div>
            ))}
          </div>
        </div>

      </div>

      <div className="d-flex align-items-center gap-3 mt-2">
        <button className="btn btn-sm btn-success ms-auto"
          onClick={() => onGerar({ desfecho, metrica: isAbandono ? "ABANDONO" : metrica, anos, regiao: regiao || null, estado: estado || null })}>
          Gerar Ranking
        </button>
        <button className="btn btn-sm btn-outline-secondary"
          onClick={() => { setRegiao(""); setEstado(""); setAnos(["TODOS"]); setDesfecho("TODOS"); setMetrica("INCIDENCIA"); }}>
          Limpar
        </button>
      </div>
    </div>
  );
}

// ── Tabela de Ranking ─────────────────────────────────────────────────────────
const TabelaRanking = memo(function TabelaRanking({ rows, tipo, metrica }) {
  const isAlta = tipo === "alta";
  const cor    = isAlta ? "#dc3545" : "#198754";
  const titulo = isAlta ? "🔴 Criticidade Alta — Top 10 piores" : "🟢 Criticidade Baixa — Top 10 melhores";

  const labelValor = useMemo(() => {
    if (metrica === "INCIDENCIA") return "Incidência /100k";
    if (metrica === "ABANDONO")   return "Taxa Abandono (%)";
    return "Nº de Casos";
  }, [metrica]);

  if (!rows?.length) return null;

  return (
    <div className="card mb-3" style={{ borderTop: `3px solid ${cor}` }}>
      <div className="card-header" style={{ fontWeight: 700, color: cor, padding: "8px 14px" }}>
        {titulo}
      </div>
      <div style={{ overflowY: "auto", maxHeight: 420 }}>
        <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.82rem" }}>
          <thead style={{ position: "sticky", top: 0, background: "var(--card-bg,#fff)", zIndex: 1 }}>
            <tr>
              <th>#</th>
              <th>Município — UF</th>
              <th>{labelValor}</th>
              <th>PIB per capita</th>
              <th>Saneamento (%)</th>
              <th>Dens. Demog.</th>
              <th>Estab. Saúde</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row.NOME_MUNIC}-${row.SIGLA_UF}-${i}`}>
                <td style={{ color: cor, fontWeight: 700 }}>{i + 1}</td>
                <td>{row.NOME_MUNIC} — {row.SIGLA_UF}</td>
                <td><strong>{row.Valor != null ? row.Valor.toLocaleString("pt-BR") : "—"}</strong></td>
                <td>{row.IBGE_PIB_PER_CAPITA != null ? `R$ ${Number(row.IBGE_PIB_PER_CAPITA).toLocaleString("pt-BR")}` : "—"}</td>
                <td>{row.IBGE_ESGOTAMENTO_SANITARIO_ADEQUADO != null ? `${Number(row.IBGE_ESGOTAMENTO_SANITARIO_ADEQUADO).toFixed(1)}%` : "—"}</td>
                <td>{row.IBGE_DENSIDADE_DEMOGRAFICA != null ? Number(row.IBGE_DENSIDADE_DEMOGRAFICA).toLocaleString("pt-BR") : "—"}</td>
                <td>{row.QT_ESTABELECIMENTOS_TOTAL != null ? row.QT_ESTABELECIMENTOS_TOTAL : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// ── Painel de Insights ────────────────────────────────────────────────────────
const PainelInsights = memo(function PainelInsights({ data }) {
  if (!data?.length) return null;

  return (
    <div className="card mb-3">
      <div className="card-header" style={{ fontWeight: 700, padding: "8px 14px" }}>
        💡 Painel de Atenção Prioritária
        <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.8rem" }}>
          Municípios com alta incidência e baixos indicadores socioeconômicos
        </span>
      </div>
      <div className="card-body">
        <div className="row g-3">
          {data.map((reg) => (
            <div key={reg.regiao} className="col-12 col-md-6 col-xl-4">
              <div className="card h-100" style={{ borderLeft: `4px solid ${reg.n_criticos > 0 ? "#ff6b35" : "#198754"}` }}>
                <div className="card-body" style={{ padding: "12px 14px" }}>
                  <h6 className="card-title mb-2" style={{ fontWeight: 700 }}>{reg.regiao}</h6>
                  <div style={{ fontSize: "0.82rem", lineHeight: 1.7 }}>
                    <div>
                      <span className="text-muted">Municípios críticos: </span>
                      <strong style={{ color: reg.n_criticos > 0 ? "#dc3545" : "#198754" }}>
                        {reg.n_criticos}
                      </strong>
                    </div>
                    <div>
                      <span className="text-muted">Incidência média: </span>
                      <strong>{reg.incidencia_media?.toLocaleString("pt-BR")} /100k</strong>
                    </div>
                    {reg.pib_medio != null && (
                      <div>
                        <span className="text-muted">PIB médio: </span>
                        <strong>R$ {Number(reg.pib_medio).toLocaleString("pt-BR")}</strong>
                      </div>
                    )}
                    {reg.saneamento_medio != null && (
                      <div>
                        <span className="text-muted">Saneamento médio: </span>
                        <strong>{reg.saneamento_medio?.toFixed(1)}%</strong>
                      </div>
                    )}
                    {reg.municipios_criticos?.length > 0 && (
                      <div className="mt-2">
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                          {reg.municipios_criticos.slice(0, 3).map((m) => `${m.NOME_MUNIC}/${m.SIGLA_UF}`).join(", ")}
                          {reg.municipios_criticos.length > 3 && ` +${reg.municipios_criticos.length - 3}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

// ── Página principal ──────────────────────────────────────────────────────────
export default function RankingTabular() {
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [params, setParams]           = useState(null);
  const [dismissedLoading, setDismissedLoading] = useState(false);

  const body = useMemo(() => params ? {
    desfecho: params.desfecho,
    metrica:  params.metrica,
    anos:     params.anos,
    regiao:   params.regiao,
    estado:   params.estado,
  } : null, [params]);

  const qk = params
    ? [params.desfecho, params.metrica, params.anos.join(","),
       params.regiao ?? "", params.estado ?? ""]
    : [];

  const enabled = !!params;

  const tabelaQ   = useQuery({ queryKey: ["rank-tabular",  ...qk], queryFn: () => fetchDataPost("/api/ranking/tabular",  body), enabled, staleTime: STALE, refetchOnWindowFocus: false });
  const insightsQ = useQuery({ queryKey: ["rank-insights", ...qk], queryFn: () => fetchDataPost("/api/ranking/insights", body), enabled, staleTime: STALE, refetchOnWindowFocus: false });

  const anyLoading = tabelaQ.isLoading || insightsQ.isLoading;

  return (
    <section className="container-fluid dashboard px-0">
      <div className="titulo p-3 d-flex justify-content-between align-items-center">
        <div className="titulo-nome">Ranking Tabular</div>
        <button className="filter-button" onClick={() => setFiltersOpen((o) => !o)}>
          Filtros
        </button>
      </div>

      <div className="px-3 pt-3">
        {filtersOpen && (
          <RankingFilters
            onGerar={(p) => {
              setParams(p);
              setDismissedLoading(false);
              setFiltersOpen(false);
            }}
          />
        )}
      </div>

      <LoadingModal
        show={enabled && anyLoading && !dismissedLoading}
        onHide={() => setDismissedLoading(true)}
      />

      {params && !anyLoading && (
        <div className="px-3">
          <div className="row g-3 mb-3">
            <div className="col-12 col-xl-6">
              <TabelaRanking rows={tabelaQ.data?.alta}  tipo="alta"  metrica={params.metrica} />
            </div>
            <div className="col-12 col-xl-6">
              <TabelaRanking rows={tabelaQ.data?.baixa} tipo="baixa" metrica={params.metrica} />
            </div>
          </div>

          <PainelInsights data={insightsQ.data} />
        </div>
      )}
    </section>
  );
}
